import { useCallback, useEffect, useState } from 'react';
import { useEchap } from '../partage/useEchap';
import './Hierarchiser.css';

/**
 * Hiérarchiser — SPEC §5.4 : *« une touche, puis on tape dans l'ordre : 1, 2, 3, 4. »*
 *
 * **C'est le seul champ qui porte une connaissance que personne d'autre n'a.** Un template,
 * un modèle ou un collègue peut donner la liste et les délais. L'ordre dans
 * lequel *toi* tu t'y prends dépend de ta vie, et personne ne peut l'écrire à ta place —
 * c'est la seule couche que l'app garde et qu'un plan collé dans un fichier perd.
 *
 * Le geste est **le même au doigt et à la souris** (principe 8), et plus rapide qu'un
 * glisser-déposer : on désigne dans l'ordre, on ne traîne rien.
 *
 * À ne pas confondre avec la priorité (chantier `priorites`) : la priorité dit ce que
 * « Maintenant » présente en premier, le rang dit l'ordre dans lequel TU construis ton
 * projet. Hiérarchiser numérote la feuille entière d'un seul geste : il n'y a plus de
 * sections à ordonner entre elles, et la carte se lit dans cet ordre (ticket 04).
 */
export function useHierarchisation(
  surEnregistrer: (rangs: Map<string, number>) => void,
  /**
   * La touche « h » lance Hiérarchiser ; `false` : elle ne fait rien, comme en Analyse sur la
   * Carte et la Hiérarchie (chantier theme-analyse, ticket 01). Le menu ⚙ le lance toujours.
   */
  ecouteH = true
) {
  const [actif, setActif] = useState(false);
  const [ordre, setOrdre] = useState<string[]>([]);

  const arreter = useCallback(() => {
    setActif(false);
    setOrdre([]);
  }, []);

  // Échap arrête, sans rien poser ; le mode est à côté de tout, comme l'Aide.
  useEchap(actif, arreter, 'a-cote');

  // La touche « h » lance ou arrête le mode : une touche à elle, pas Échap.
  useEffect(() => {
    if (!ecouteH) return;
    const auClavier = (evenement: KeyboardEvent) => {
      const cible = evenement.target as HTMLElement | null;
      // On n'attrape jamais « h » pendant qu'on écrit : la barre d'ajout ne se ferme jamais,
      // donc le curseur y est presque toujours.
      const enTrainDEcrire =
        cible?.tagName === 'INPUT' ||
        cible?.tagName === 'TEXTAREA' ||
        cible?.isContentEditable === true;

      if (evenement.key === 'h' && !enTrainDEcrire && !evenement.metaKey && !evenement.ctrlKey) {
        evenement.preventDefault();
        setActif((avant) => !avant);
        setOrdre([]);
      }
    };

    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [ecouteH]);

  /** Désigner une bulle lui donne le numéro suivant. La redésigner l'enlève. */
  const designer = useCallback((id: string) => {
    setOrdre((avant) => (avant.includes(id) ? avant.filter((n) => n !== id) : [...avant, id]));
  }, []);

  const rangs = new Map(ordre.map((id, i) => [id, i + 1]));

  /** N'envoie que les désignées : le serveur range les autres à la suite (chantier `bugs`, ticket 03). */
  function enregistrer() {
    if (ordre.length > 0) surEnregistrer(rangs);
    arreter();
  }

  return {
    actif,
    /** Les numéros posés jusqu'ici. `null` quand le mode est éteint. */
    rangs: actif ? rangs : null,
    designer,
    enregistrer,
    arreter,
    commencer: () => {
      setActif(true);
      setOrdre([]);
    },
  };
}

export function BarreHierarchisation({
  poses,
  total,
  onEnregistrer,
  onRenoncer,
}: {
  poses: number;
  total: number;
  onEnregistrer: () => void;
  onRenoncer: () => void;
}) {
  return (
    <div className="hierarchiser" role="status">
      <div className="hierarchiser-mot">
        <strong>Tape dans ton ordre.</strong>
        <span>
          Désigne-les une à une, en commençant par ce que tu ferais en premier. Redésigner
          enlève le numéro.
        </span>
      </div>

      <span className="hierarchiser-compte">
        {poses} sur {total}
      </span>

      {/* Le geste appartient au dossier : il porte SA couleur, jamais le bleu-vert d'action
          (AGENTS, règle 3). Sinon les cinq dossiers se hiérarchisent de la même couleur. */}
      <button className="hierarchiser-poser" onClick={onEnregistrer} disabled={poses === 0}>
        C’est dans cet ordre
      </button>

      <div className="hierarchiser-renoncer">
        <button type="button" onClick={onRenoncer}>
          Laisser comme c’était
        </button>
        <span>Touche échap</span>
      </div>
    </div>
  );
}
